
import type { BreakpointProp } from "@/ui/lib/utils";
import { HTMLAttributes, PropsWithChildren } from "react";

/*-------------------------------------
|  Constants
|-------------------------------------*/


export const TYPOGRAPHY_ALIGNMENTS_CLASS_MAP = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
  inherit: "text-inherit",
} as const;

export const TYPOGRAPHY_RESPONSIVE_ALIGNMENTS_CLASS_MAP = {
  default: TYPOGRAPHY_ALIGNMENTS_CLASS_MAP,
  xl: {
    left: "xl:text-left",
    center: "xl:text-center",
    right: "xl:text-right",
    inherit: "xl:text-inherit",
  },
} as const;

export const TYPOGRAPHY_WHITESPACES_CLASS_MAP = {
  normal: "whitespace-normal",
  noWrap: "whitespace-nowrap",
  balance: "whitespace-normal text-balance",
} as const;

export const TYPOGRAPHY_WEIGHTS_CLASS_MAP = {
  normal: "font-normal",
  bold: "font-bold",
} as const;

export const TYPOGRAPHY_COLORS_CLASS_MAP = {
  inherit: "text-inherit",
  default: "text-foreground",
  inverse: "text-foreground-inverse",
  secondary: "text-foreground-muted-strong",
  tertiary: "text-foreground-muted",
  disabled: "text-foreground-muted opacity-50",
  error: "text-destructive",
  success: "text-success",
} as const;

/*-------------------------------------
|  Types
|-------------------------------------*/

export type TTypographyTag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "address" | "span" | "div" | "strong" | "i" | "b" | "u" | "s" | "blockquote" | "figcaption" | "del" | "label";

export type TTypographyAlign = keyof typeof TYPOGRAPHY_ALIGNMENTS_CLASS_MAP;

export type TTypographyAlignProp =
  BreakpointProp<typeof TYPOGRAPHY_RESPONSIVE_ALIGNMENTS_CLASS_MAP>;

export type TTypographyAlignSubsetProp<TAlign extends TTypographyAlign> =
  BreakpointProp<typeof TYPOGRAPHY_RESPONSIVE_ALIGNMENTS_CLASS_MAP, TAlign>;

export type TTypographyWhitespace = keyof typeof TYPOGRAPHY_WHITESPACES_CLASS_MAP;

export type TTypographyWeight = keyof typeof TYPOGRAPHY_WEIGHTS_CLASS_MAP;

export type TTypographyColor = keyof typeof TYPOGRAPHY_COLORS_CLASS_MAP;

export type TBaseTypographyProps = {
  isResponsive?: boolean;
} & PropsWithChildren & HTMLAttributes<HTMLElement>;


/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const BASE_TYPOGRAPHY_DEFAULTS = {
  isResponsive: true,
} satisfies TBaseTypographyProps;
